import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Eye, Plus, Trash2, Users, Globe, User } from 'lucide-react';
import { GridView, GridColumn, SortState, GridPagination } from '../../../types/datagrid';

interface ViewManagerProps {
  isOpen: boolean;
  onClose: () => void;
  currentView?: GridView;
  views: GridView[];
  onViewChange: (viewId: string) => void;
  onViewSave: (view: Partial<GridView>) => void;
  onViewDelete: (viewId: string) => void;
  onViewReset: () => void;
  gridState: {
    columns: GridColumn[];
    sorting: SortState[];
    filters: Record<string, any>;
    pagination: GridPagination;
  };
  permissions?: {
    canCreateView?: boolean;
    canEditView?: boolean;
    canDeleteView?: boolean;
  };
}

export const ViewManager: React.FC<ViewManagerProps> = ({
  isOpen,
  onClose,
  currentView,
  views,
  onViewChange,
  onViewSave,
  onViewDelete,
  gridState,
  permissions = {},
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [newViewDescription, setNewViewDescription] = useState('');
  const [newViewType, setNewViewType] = useState<'personal' | 'role' | 'public'>('personal');

  if (!isOpen) return null;

  const getViewIcon = (type: 'personal' | 'role' | 'public') => {
    switch (type) {
      case 'personal':
        return <User className="h-4 w-4" />;
      case 'role':
        return <Users className="h-4 w-4" />;
      case 'public':
        return <Globe className="h-4 w-4" />;
    }
  };

  const getViewTypeColor = (type: 'personal' | 'role' | 'public') => {
    switch (type) {
      case 'personal':
        return 'text-blue-600 bg-blue-100';
      case 'role':
        return 'text-green-600 bg-green-100';
      case 'public':
        return 'text-purple-600 bg-purple-100';
    }
  };

  const handleCreateView = () => {
    if (!newViewName.trim()) return;

    onViewSave({
      name: newViewName.trim(),
      description: newViewDescription.trim() || undefined,
      type: newViewType,
      columns: gridState.columns,
      sorting: gridState.sorting,
      filters: gridState.filters,
      pagination: gridState.pagination,
      isDefault: false,
    });

    setNewViewName('');
    setNewViewDescription('');
    setNewViewType('personal');
    setShowCreateForm(false);
  };

  const getViewSummary = (view: GridView) => {
    const summary = [];

    if (view.sorting.length > 0) {
      summary.push(`${view.sorting.length} sort${view.sorting.length > 1 ? 's' : ''}`);
    }

    const filterCount = Object.values(view.filters).filter(
      v => v !== null && v !== undefined && v !== '' && (!Array.isArray(v) || v.length > 0)
    ).length;

    if (filterCount > 0) {
      summary.push(`${filterCount} filter${filterCount > 1 ? 's' : ''}`);
    }

    const hiddenColumns = view.columns.filter(c => !c.isVisible).length;
    if (hiddenColumns > 0) {
      summary.push(`${hiddenColumns} hidden column${hiddenColumns > 1 ? 's' : ''}`);
    }

    return summary.length > 0 ? summary.join(', ') : 'Default settings';
  };

  const personalViews = views.filter(v => v.type === 'personal');
  const roleViews = views.filter(v => v.type === 'role');
  const publicViews = views.filter(v => v.type === 'public');

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[60%] max-w-2xl animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 bg-white rounded-t-2xl flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="h-8 w-8 bg-brand-100 rounded-lg flex items-center justify-center">
                <Eye className="h-4 w-4 text-brand-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Manage Views</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
            {/* Current View Info */}
            {currentView && (
              <div className="p-4 bg-brand-50 border border-brand-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center space-x-2">
                      {getViewIcon(currentView.type)}
                      <h3 className="text-sm font-medium text-gray-900">
                        Current View: {currentView.name}
                      </h3>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">{getViewSummary(currentView)}</p>
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${getViewTypeColor(currentView.type)}`}
                  >
                    {currentView.type}
                  </span>
                </div>
              </div>
            )}

            {/* Create New View */}
            {permissions.canCreateView && (
              <div className="space-y-4">
                {!showCreateForm ? (
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(true)}
                    className="flex items-center space-x-2 px-4 py-3 text-sm text-brand-600 hover:text-brand-700 hover:bg-brand-50 rounded-lg border border-dashed border-brand-300 hover:border-brand-400 transition-colors w-full"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Save Current State as New View</span>
                  </button>
                ) : (
                  <div className="p-4 border border-gray-200 rounded-lg space-y-4">
                    <h4 className="text-sm font-medium text-gray-900">Create New View</h4>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        View Name *
                      </label>
                      <input
                        type="text"
                        value={newViewName}
                        onChange={e => setNewViewName(e.target.value)}
                        placeholder="Enter view name"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        autoFocus
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={newViewDescription}
                        onChange={e => setNewViewDescription(e.target.value)}
                        placeholder="Optional description"
                        rows={2}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        View Type
                      </label>
                      <select
                        value={newViewType}
                        onChange={e => setNewViewType(e.target.value as any)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      >
                        <option value="personal">Personal (only you can see)</option>
                        <option value="role">Role (team members can see)</option>
                        <option value="public">Public (everyone can see)</option>
                      </select>
                    </div>

                    <div className="flex items-center justify-end space-x-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowCreateForm(false);
                          setNewViewName('');
                          setNewViewDescription('');
                        }}
                        className="px-3 py-2 text-sm text-gray-700 hover:text-gray-900"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleCreateView}
                        disabled={!newViewName.trim()}
                        className="px-3 py-2 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Save View
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Personal Views */}
            {personalViews.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center space-x-2">
                  <User className="h-4 w-4" />
                  <span>Personal Views</span>
                </h3>
                <div className="space-y-2">
                  {personalViews.map(view => (
                    <ViewItem
                      key={view.id}
                      view={view}
                      isActive={currentView?.id === view.id}
                      onSelect={() => onViewChange(view.id)}
                      onDelete={() => onViewDelete(view.id)}
                      canDelete={permissions.canDeleteView}
                      summary={getViewSummary(view)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Role Views */}
            {roleViews.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center space-x-2">
                  <Users className="h-4 w-4" />
                  <span>Team Views</span>
                </h3>
                <div className="space-y-2">
                  {roleViews.map(view => (
                    <ViewItem
                      key={view.id}
                      view={view}
                      isActive={currentView?.id === view.id}
                      onSelect={() => onViewChange(view.id)}
                      onDelete={() => onViewDelete(view.id)}
                      canDelete={false} // Role views typically can't be deleted by regular users
                      summary={getViewSummary(view)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Public Views */}
            {publicViews.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center space-x-2">
                  <Globe className="h-4 w-4" />
                  <span>Public Views</span>
                </h3>
                <div className="space-y-2">
                  {publicViews.map(view => (
                    <ViewItem
                      key={view.id}
                      view={view}
                      isActive={currentView?.id === view.id}
                      onSelect={() => onViewChange(view.id)}
                      onDelete={() => onViewDelete(view.id)}
                      canDelete={false} // Public views typically can't be deleted by regular users
                      summary={getViewSummary(view)}
                    />
                  ))}
                </div>
              </div>
            )}

            {views.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Eye className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-sm">No saved views yet</p>
                <p className="text-xs text-gray-400">Create your first view to get started</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-6 bg-gray-50 rounded-b-2xl flex-shrink-0">
            <div className="flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

// Helper component for view items
interface ViewItemProps {
  view: GridView;
  isActive: boolean;
  onSelect: () => void;
  onDelete: () => void;
  canDelete: boolean;
  summary: string;
}

const ViewItem: React.FC<ViewItemProps> = ({
  view,
  isActive,
  onSelect,
  onDelete,
  canDelete,
  summary,
}) => (
  <div
    className={`
      flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors
      ${
        isActive
          ? 'bg-brand-100 border border-brand-300'
          : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
      }
    `}
    onClick={onSelect}
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center space-x-2">
        <h4
          className={`text-sm font-medium truncate ${isActive ? 'text-brand-900' : 'text-gray-900'}`}
        >
          {view.name}
        </h4>
        {view.isDefault && (
          <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full">
            Default
          </span>
        )}
      </div>
      <p className="text-xs text-gray-600 mt-1">{summary}</p>
      {view.description && <p className="text-xs text-gray-500 mt-1">{view.description}</p>}
    </div>

    {canDelete && (
      <button
        type="button"
        onClick={e => {
          e.stopPropagation();
          onDelete();
        }}
        className="ml-2 p-1 text-gray-400 hover:text-red-600 rounded"
        title="Delete view"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    )}
  </div>
);
